import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import {
  getTimeTodayWithTime,
  parseStringToNumber,
  renderValue,
  returnIndexQuotes,
} from './functions';

import { socketUpdateIndexQuote } from 'store/AnalyticsCenter';
import { updateCoveredWarrants } from 'store/AnalyticsCenter/businessNews';
import { updateStockInformationSocket } from 'store/AnalyticsCenter/stockInformation';
import {
  socketUpdateOrderMatching,
  socketUpdatePrice,
} from 'store/AnalyticsCenter/transactionHistory';
import { useAppSelector, useAppDispatch } from 'store/hooks';

// using jquery for signalr
const $ = require('jquery');

(window as any).jQuery = $;
require('signalr');

const connection = $.hubConnection(process.env.REACT_APP_SOCKET_URL);
export const hubProxy = connection.createHubProxy('pbhub');

const SocketConnection: React.FC = () => {
  const { search } = useLocation();
  const dispatch = useAppDispatch();
  const query = new URLSearchParams(search);
  const stockSymbol = useAppSelector(
    (state) => state.analyticsCenter.analyticCenter?.stockSymbol,
  );
  const indexQuotesSocketData = useAppSelector(
    (state) => state.analyticsCenter.indexQuotesSocketData,
  );

  const indexCodesRef = useRef<string[]>([]);

  const pthMktId = '';

  const updateIndexQuote = (data: SocketResponseTypes) => {
    for (let index = 0; index < data.mi.length; index += 1) {
      const element = data.mi[index][0];
      if (element !== null) {
        const isExist = !element.a
          ? -1
          : indexCodesRef.current.findIndex(
            (item) => item === returnIndexQuotes(element.a),
          );
        if (isExist > -1 && element.h !== 'noChange') {
          dispatch(
            socketUpdateIndexQuote({
              data: {
                ...indexQuotesSocketData[isExist],
                exchangeCode: returnIndexQuotes(element.a),
                value: Number(element.e),
                changeValue: Number(element.f),
                changeValuePercent: Number(element.g),
              },
              index: isExist,
            }),
          );
        }
      }
    }
  };

  useEffect(() => {
    connection
      .start({ transport: 'webSockets', jsonp: true, pingInterval: 30000 })
      .done(() => {
        hubProxy.invoke(
          'GetAllStocks',
          `${query.get('stock_symbol') || ''}`,
          0,
          true,
          false,
          '',
          pthMktId,
          false,
          true,
          '',
        );
      })
      .fail(() => {
        // eslint-disable-next-line no-console
        console.log('Could not connect');
      });
    hubProxy.on('updateStockPrice', (data: SocketResponseTypes) => {
      if (data.pb && data.pb.f) {
        const dataMapping = data.pb.f
          .map((item) => JSON.parse(item) as StockInfoTypes)
          .find((t) => t.A === query.get('stock_symbol'));
        const timeMapping = data.tl?.f?.filter(
          (item) => item.s === query.get('stock_symbol'),
        );
        if (dataMapping && timeMapping.length) {
          const lastest = timeMapping.length - 1;
          dispatch(
            updateStockInformationSocket({
              stock: dataMapping,
              time: timeMapping,
            }),
          );
          dispatch(
            socketUpdatePrice({
              price: parseStringToNumber(timeMapping[lastest].f.i[1]),
              priceChange: parseStringToNumber(timeMapping[lastest].f.i[2]),
              priceChangePercent:
                (parseStringToNumber(timeMapping[lastest].f.i[2])
                  / parseStringToNumber(dataMapping.RE))
                * 100,
            }),
          );
          dispatch(
            updateCoveredWarrants(
              data.pb.f.map((item) => JSON.parse(item) as StockInfoTypes),
            ),
          );

          // transaction history

          // OverallData
          const pricePercent = (parseStringToNumber(timeMapping[lastest].f.i[2])
                / parseStringToNumber(dataMapping.RE))
              * 100;
          const newOveralData = {
            reference: renderValue(parseStringToNumber(dataMapping.RE)),
            min: renderValue(parseStringToNumber(dataMapping.Z)),
            max: renderValue(parseStringToNumber(dataMapping.Y)),
            open: renderValue(parseStringToNumber(dataMapping.X)),
            current: renderValue(parseStringToNumber(timeMapping[lastest].f.i[1])),
            percent: `${renderValue(
              parseStringToNumber(timeMapping[lastest].f.i[2]),
            )} (${renderValue(pricePercent)}%)`,
            increase: parseStringToNumber(timeMapping[lastest].f.i[2]) >= 0,
            total: renderValue(parseStringToNumber(dataMapping.Q)),
          };

          // OverallTableData
          const newOverallTableData = timeMapping.map((item) => ({
            price: parseStringToNumber(item.f.i[1]).toString(),
            amount: parseStringToNumber(item.f.i[3]).toString(),
            rate: renderValue(
              parseStringToNumber(
                `${(item.f.i[3] / parseStringToNumber(dataMapping.Q)) * 100}`,
              ),
            ),
          }));

          // DetailTable
          const newDetailData = timeMapping.map((item) => ({
            time: item.f.i[0],
            price: renderValue(parseStringToNumber(item.f.i[1])),
            amount: renderValue(parseStringToNumber(item.f.i[3])),
            amountSaved: item.f.i[4],
            percent: `${item.f.i[2]} (${renderValue((parseStringToNumber(item.f.i[2])
                    / parseStringToNumber(dataMapping.RE))
                  * 100)}%)`,
            increase: parseStringToNumber(item.f.i[2]) >= 0,
            rate: renderValue(
              parseStringToNumber(
                `${(item.f.i[3] / parseStringToNumber(dataMapping.Q)) * 100}`,
              ),
            ),
          }));

          // chart
          const dataConvert = timeMapping.map((item) => ({
            price: parseStringToNumber(item.f.i[1]),
            volume: parseStringToNumber(item.f.i[3]),
            priceChangePercent: parseStringToNumber(dataMapping.Q),
            priceChange: parseStringToNumber(item.f.i[2]),
            sessionDate: new Date(
              getTimeTodayWithTime(item.f.i[0]),
            ).toISOString(),
          }));

          dispatch(
            socketUpdateOrderMatching({
              newOverall: newOveralData,
              newOverallItemTable: newOverallTableData,
              newItemDetail: newDetailData,
              newChartItem: dataConvert,
            }),
          );
        }
      }
      if (data.mi && data.mi.length && indexCodesRef.current.length) {
        updateIndexQuote(data);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (stockSymbol && connection.state === 1) {
      hubProxy.invoke(
        'UpdateParams',
        `${stockSymbol}`,
        0,
        true,
        true,
        `${stockSymbol}`,
        pthMktId,
        false,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stockSymbol]);

  useEffect(() => {
    indexCodesRef.current = Array.from(
      indexQuotesSocketData,
      (item) => item.exchangeCode,
    );
  }, [indexQuotesSocketData]);

  return null;
};

export default SocketConnection;
